import { useCallback, type ReactNode } from "react";
import { Activity, Gavel, ShieldCheck } from "lucide-react";

import { ObservabilityService } from "@/api";
import { useApi } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { usePoll } from "@/hooks/use-poll";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import {
  formatCompactNumberID,
  formatMilliseconds,
  formatRelativeTimeID,
} from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";

const POLL_INTERVAL_MS = 30_000;

interface TileProps {
  label: string;
  icon: ReactNode;
  value: string;
  caption?: string;
  isLoading?: boolean;
  isError?: boolean;
}

function Tile({ label, icon, value, caption, isLoading, isError }: TileProps) {
  return (
    <div
      className={
        "rounded-lg border bg-white p-4 transition-colors " +
        (isError
          ? "border-warning-600/30 bg-warning-50/30"
          : "border-ocean-100 hover:border-ocean-200")
      }
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <span className="text-ocean-400">{icon}</span>
      </div>
      {isLoading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <div className="text-2xl font-medium text-ocean-900">{value}</div>
      )}
      {caption && (
        <div className="mt-1 text-xs text-neutral-500">{caption}</div>
      )}
    </div>
  );
}

export function OverviewTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const workspaceId = user?.workspace_id ?? undefined;

  const postureFetcher = useCallback(
    () => ObservabilityService.getPostureSummary(workspaceId),
    [workspaceId]
  );
  const latencyFetcher = useCallback(
    () =>
      ObservabilityService.getInferenceLatency({
        window: "24h",
        workspace_id: workspaceId,
      }),
    [workspaceId]
  );
  const policyFetcher = useCallback(
    () =>
      ObservabilityService.getPolicyEnforcement({
        window: "7d",
        workspace_id: workspaceId,
      }),
    [workspaceId]
  );
  const auditFetcher = useCallback(
    () => ObservabilityService.getAuditLogs({ limit: 5 }),
    []
  );

  const posture = useApi(postureFetcher, { deps: [workspaceId] });
  const latency = useApi(latencyFetcher, { deps: [workspaceId] });
  const policy = useApi(policyFetcher, { deps: [workspaceId] });
  const audit = useApi(auditFetcher);

  usePoll(
    async () => {
      await Promise.allSettled([
        posture.refetch(),
        latency.refetch(),
        policy.refetch(),
        audit.refetch(),
      ]);
    },
    POLL_INTERVAL_MS,
    { enabled: Boolean(user) }
  );

  const postureScore = posture.data?.overall_posture?.score;
  const totalDecisions = policy.data?.total_decisions ?? 0;
  const deniedCount = policy.data?.by_effect?.deny ?? 0;
  const totalRequests = latency.data?.overall?.count ?? 0;
  const p99 = latency.data?.overall?.p99_ms;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
        <Tile
          label={t("observability.overview.tilePostureLabel")}
          icon={<ShieldCheck size={16} />}
          value={
            postureScore !== null && postureScore !== undefined
              ? `${Math.round(postureScore)}%`
              : "—"
          }
          caption={
            posture.data?.overall_posture?.band ??
            t("observability.overview.tilePostureNoData")
          }
          isLoading={posture.isLoading && !posture.data}
          isError={Boolean(posture.error)}
        />
        <Tile
          label={t("observability.overview.tileRequestsLabel")}
          icon={<Activity size={16} />}
          value={formatCompactNumberID(totalRequests)}
          caption={
            p99 !== undefined && p99 !== null
              ? t("observability.overview.tileRequestsP99", {
                  value: formatMilliseconds(p99),
                })
              : t("observability.overview.tileRequestsNoData")
          }
          isLoading={latency.isLoading && !latency.data}
          isError={Boolean(latency.error)}
        />
        <Tile
          label={t("observability.overview.tileDecisionsLabel")}
          icon={<Gavel size={16} />}
          value={formatCompactNumberID(totalDecisions)}
          caption={t("observability.overview.tileDecisionsDenied", {
            count: formatCompactNumberID(deniedCount),
          })}
          isLoading={policy.isLoading && !policy.data}
          isError={Boolean(policy.error)}
        />
        <Tile
          label={t("observability.overview.tileAuditLabel")}
          icon={<Activity size={16} />}
          value={
            audit.data?.items?.length
              ? formatCompactNumberID(audit.data.items.length)
              : "0"
          }
          caption={t("observability.overview.tileAuditCaption")}
          isLoading={audit.isLoading && !audit.data}
          isError={Boolean(audit.error)}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 desktop:grid-cols-2">
        <Section
          title={t("observability.overview.concernsTitle")}
          description={t("observability.overview.concernsDescription")}
        >
          {posture.error ? (
            <Alert variant="warning">{parseApiError(posture.error)}</Alert>
          ) : posture.isLoading && !posture.data ? (
            <Skeleton className="h-32 w-full" />
          ) : posture.data && posture.data.top_concerns.length > 0 ? (
            <ul className="space-y-2 rounded-lg border border-ocean-100 bg-white p-3">
              {posture.data.top_concerns.slice(0, 5).map((concern, idx) => (
                <li
                  key={`${concern.type}-${idx}`}
                  className="flex items-start justify-between gap-3 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-ocean-900">
                      {concern.type}
                    </div>
                    {concern.detail && (
                      <div className="text-xs text-neutral-500">
                        {concern.detail}
                      </div>
                    )}
                  </div>
                  <Badge
                    tone={
                      concern.priority === "critical"
                        ? "danger"
                        : concern.priority === "high"
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {concern.priority}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-lg border border-ocean-100 bg-white p-5 text-sm text-neutral-500">
              {t("observability.overview.concernsEmpty")}
            </div>
          )}
        </Section>

        <Section
          title={t("observability.overview.recentAuditTitle")}
          description={t("observability.overview.recentAuditDescription")}
        >
          {audit.error ? (
            <Alert variant="warning">{parseApiError(audit.error)}</Alert>
          ) : audit.isLoading && !audit.data ? (
            <Skeleton className="h-32 w-full" />
          ) : audit.data && audit.data.items.length > 0 ? (
            <ul className="divide-y divide-ocean-100/60 rounded-lg border border-ocean-100 bg-white">
              {audit.data.items.slice(0, 5).map((item) => (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-ocean-900">
                      {item.action}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {item.path ?? "—"}
                      {item.status_code && ` · ${item.status_code}`}
                    </div>
                  </div>
                  <div className="shrink-0 text-xs text-neutral-500">
                    {item.created_at
                      ? formatRelativeTimeID(item.created_at)
                      : "—"}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-lg border border-ocean-100 bg-white p-5 text-sm text-neutral-500">
              {t("observability.overview.recentAuditEmpty")}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
